import { useState, useEffect, useRef, ReactNode } from "react";
import { createPortal } from "react-dom";
import { getBlogPost, formatBlogDate } from "./blog-content";
import { SEOHead } from "./SEOHead";
import { Copy, Check, Link2, Clock } from "lucide-react";
import { MDXProvider } from "@mdx-js/react";
import { blogPosts } from "./blog-content";
import { SiteNavbar } from "@/components/SiteNavbar";
import { SiteLink } from "@/components/SiteLink";
import {
  Conversation,
  TabbedConversation,
  TabbedConversationSSR,
  ConversationTab,
  AppWidget,
  AppWidgetList,
  AppWidgetImage,
  AppWidgetTrackList,
  AppWidgetCart,
  AppWidgetConfirmation,
} from "@/client-integrations/Conversation";
import { Diagram } from "@/components/Diagram";
import { useParams, useLocation } from "wouter";
import { mountIslands } from "./island-mounter";

const SITE_URL = "https://taias.xyz";

// ============================================================================
// Helper: Check for server-rendered static content
// ============================================================================

function useStaticBlogRoot(slug: string | undefined): HTMLElement | null {
  const [staticRoot, setStaticRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!slug) {
      setStaticRoot(null);
      return;
    }

    // Check if server-rendered content exists for this slug
    const root = document.getElementById("blog-static-root");
    if (root && root.dataset.slug === slug) {
      setStaticRoot(root);
    } else {
      setStaticRoot(null);
    }
  }, [slug]);

  return staticRoot;
}

// ============================================================================
// CodeBlock component with copy button (for CSR fallback)
// ============================================================================

function CodeBlock({ children, ...props }: { children?: ReactNode }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const wrapper = (e.currentTarget as HTMLElement).parentElement;
    const pre = wrapper?.querySelector("pre");
    const text = pre?.textContent || "";

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="code-block-wrapper relative group my-6">
      <pre
        {...props}
        className="rounded-xl overflow-hidden p-5 overflow-x-auto text-[13px] leading-relaxed font-mono text-zinc-800 dark:text-zinc-200 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800"
      >
        {children}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute right-3 top-3 z-10 p-1.5 rounded-md bg-zinc-200 hover:bg-zinc-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Copy code"
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-500 dark:text-green-400" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}

// ============================================================================
// ThemedImage component (for CSR fallback)
// ============================================================================

function ThemedImage({
  lightSrc,
  darkSrc,
  alt,
  className = "",
}: {
  lightSrc: string;
  darkSrc: string;
  alt: string;
  className?: string;
}) {
  return (
    <div className={`my-6 ${className}`}>
      {/* Light mode image */}
      <img
        src={lightSrc}
        alt={alt}
        className="w-full rounded-xl border border-zinc-200 dark:hidden"
      />
      {/* Dark mode image */}
      <img
        src={darkSrc}
        alt={alt}
        className="w-full rounded-xl border border-zinc-800 hidden dark:block"
      />
    </div>
  );
}

// ============================================================================
// MDX components for CSR fallback
// ============================================================================

const mdxComponents = {
  pre: CodeBlock,
  Conversation,
  TabbedConversation,
  TabbedConversationSSR,
  ConversationTab,
  Diagram,
  AppWidget,
  AppWidgetList,
  AppWidgetImage,
  AppWidgetTrackList,
  AppWidgetCart,
  AppWidgetConfirmation,
  ThemedImage,
};

// ============================================================================
// Keep Reading Section
// ============================================================================

function KeepReadingSection({
  currentSlug,
}: {
  currentSlug: string | undefined;
}) {
  const otherPosts = blogPosts
    .filter((p) => p.slug !== currentSlug)
    .slice(0, 3);

  if (otherPosts.length === 0) return null;

  return (
    <section className="border-t border-zinc-200 dark:border-zinc-800 mt-8">
      <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
            Keep reading
          </h2>
          <SiteLink
            href="/blog"
            className="text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
          >
            View all
          </SiteLink>
        </div>

        {/* Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {otherPosts.map((otherPost) => (
            <article key={otherPost.slug} className="group">
              <SiteLink href={`/blog/${otherPost.slug}`} className="block">
                {/* Thumbnail */}
                <div className="aspect-square rounded-xl overflow-hidden bg-zinc-100 dark:bg-zinc-800 mb-4">
                  {otherPost.ogImage ? (
                    <img
                      src={otherPost.ogImage}
                      alt={otherPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-zinc-200 to-zinc-300 dark:from-zinc-700 dark:to-zinc-800" />
                  )}
                </div>

                {/* Title */}
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2 group-hover:text-zinc-600 dark:group-hover:text-zinc-300 transition-colors">
                  {otherPost.title}
                </h3>

                {/* Category + Date */}
                <div className="flex items-center gap-3 text-sm text-zinc-500 dark:text-zinc-400">
                  {otherPost.category && (
                    <span className="text-zinc-700 dark:text-zinc-300">
                      {otherPost.category}
                    </span>
                  )}
                  <time dateTime={otherPost.date}>
                    {formatBlogDate(otherPost.date)}
                  </time>
                </div>
              </SiteLink>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Footer
// ============================================================================

function BlogFooter() {
  return (
    <footer className="border-t border-zinc-200 dark:border-zinc-800 mt-8">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between text-sm text-zinc-500 dark:text-zinc-500">
          <span>© {new Date().getFullYear()} Taias</span>
          <div className="flex items-center gap-6">
            <SiteLink
              href="/docs"
              className="hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
            >
              Docs
            </SiteLink>
            <SiteLink
              href="/blog"
              className="hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
            >
              Blog
            </SiteLink>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============================================================================
// CSR Article (fallback when no static content)
// ============================================================================

function CSRArticle({
  post,
}: {
  post: NonNullable<ReturnType<typeof getBlogPost>>;
}) {
  const [copiedLink, setCopiedLink] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleCopyLink = async () => {
    const postUrl = `${SITE_URL}/blog/${post.slug}`;
    await navigator.clipboard.writeText(postUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <main className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
      <article>
        {/* Article header */}
        <header className="mb-10 text-center">
          {/* Date + Category row */}
          <div className="flex items-center justify-center gap-4 text-sm text-zinc-500 dark:text-zinc-400 mb-10">
            <time dateTime={post.date}>{formatBlogDate(post.date)}</time>
            {post.category && (
              <SiteLink
                href={`/blog/${post.category.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-zinc-900 dark:text-zinc-100 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
              >
                {post.category}
              </SiteLink>
            )}
          </div>

          {/* Title */}
          <h1 className="text-4xl lg:text-5xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight leading-tight">
            {post.title}
          </h1>

          {/* Meta row */}
          <div className="flex items-center justify-between text-sm text-zinc-500 dark:text-zinc-500 mt-20">
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              <span>{post.readTime} min read</span>
            </span>
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors cursor-pointer"
            >
              {copiedLink ? (
                <>
                  <Check className="h-4 w-4" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Link2 className="h-4 w-4" />
                  <span>Copy URL</span>
                </>
              )}
            </button>
          </div>
        </header>

        {/* Content */}
        <div ref={contentRef} className="blog-content">
          <MDXProvider components={mdxComponents}>
            <post.Component />
          </MDXProvider>
        </div>
      </article>
    </main>
  );
}

// ============================================================================
// Main BlogPostPage Component
// ============================================================================

export function BlogPostPage() {
  const params = useParams<{ slug: string }>();
  const [, navigate] = useLocation();
  const slug = params.slug;

  const post = slug ? getBlogPost(slug) : undefined;
  const staticRoot = useStaticBlogRoot(slug);

  // Mount islands into static content when it exists
  useEffect(() => {
    if (staticRoot) {
      const cleanup = mountIslands(staticRoot);
      return cleanup;
    }
  }, [staticRoot]);

  // Scroll to top on mount or when slug changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  // Redirect if post not found
  if (!post) {
    navigate("/blog");
    return null;
  }

  const postUrl = `${SITE_URL}/blog/${post.slug}`;

  // Two-root architecture:
  // - If #blog-static-root exists with our slug, the article content is already in the DOM
  // - We only render navbar, "Keep Reading", and footer
  // - If no static content, fall back to full CSR rendering

  // Sync check: on first paint (before useEffect runs) the server may have already
  // injected #blog-static-root. Use it for layout so we never render CSR + footer into #root
  // on that first paint (avoids flash / footer-above-article).
  // When slug is available, require match so client-side nav to another post shows CSR.
  // When slug is not yet available (router timing), treat element presence as static content.
  const hasStaticContentFromDOM =
    typeof document !== "undefined" &&
    (() => {
      const el = document.getElementById("blog-static-root");
      if (!el) return false;
      if (slug) return el.dataset.slug === slug;
      return true;
    })();
  const hasStaticContent = staticRoot !== null || hasStaticContentFromDOM;
  const appendTarget =
    typeof document !== "undefined"
      ? document.getElementById("blog-react-append")
      : null;

  // #region agent log
  useEffect(() => {
    const wrapper = document.getElementById("blog-page-wrapper");
    const appendEl = document.getElementById("blog-react-append");
    const nav = document.querySelector("nav");
    const logos = document.querySelectorAll('nav img[alt="Taias"]');
    const rootEl = document.getElementById("root");
    fetch("http://127.0.0.1:7242/ingest/a2b93c62-e2e6-46fd-8983-8c35a03c2db9", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        location: "BlogPostPage.tsx:effect",
        message: "layout-check",
        data: {
          hasStaticContent,
          wrapperExists: !!wrapper,
          wrapperClasses: wrapper?.className,
          wrapperPaddingTop: wrapper?.style.paddingTop,
          appendInsideWrapper: wrapper?.contains(appendEl),
          navPosition: nav ? getComputedStyle(nav).position : null,
          navHeight: nav?.getBoundingClientRect().height,
          rootHeight: rootEl?.getBoundingClientRect().height,
          logoDims: Array.from(logos).map((i) => ({
            w: i.getAttribute("width"),
            h: i.getAttribute("height"),
            dispW: i.getBoundingClientRect().width,
          })),
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
  }, [hasStaticContent]);
  // #endregion

  // When static content exists and server sent #blog-react-append, portal Keep Reading + Footer there
  // so DOM order is: #root (Navbar) | #blog-static-root (article) | #blog-react-append (Keep Reading, Footer)
  const appendContent =
    hasStaticContent && appendTarget
      ? createPortal(
          <>
            <KeepReadingSection currentSlug={slug} />
            <BlogFooter />
          </>,
          appendTarget,
        )
      : null;

  return (
    <div
      className={
        hasStaticContent ? "" : "min-h-screen bg-white dark:bg-zinc-950"
      }
    >
      <SEOHead
        title={post.title}
        description={post.description}
        canonicalUrl={postUrl}
        ogType="article"
        ogImage={post.ogImage ? `${SITE_URL}${post.ogImage}` : undefined}
        publishedTime={post.date}
        tags={post.tags}
      />

      {/* Navbar — fixed when static content exists so it stays visible while
          scrolling through content in the sibling #blog-page-wrapper */}
      <SiteNavbar
        variant="standard"
        position={hasStaticContent ? "fixed" : "sticky"}
      />

      {/* Main content: if static content exists in #blog-static-root, don't render here */}
      {hasStaticContent ? null : <CSRArticle post={post} />}

      {/* With static content + append target: portal into #blog-react-append; else render inline */}
      {hasStaticContent && appendTarget ? (
        appendContent
      ) : (
        <>
          <KeepReadingSection currentSlug={slug} />
          <BlogFooter />
        </>
      )}
    </div>
  );
}

export default BlogPostPage;
